import React, { useRef, useEffect } from "react";
import {
  Stage,
  Layer,
  Image as KonvaImage,
  Text,
  Line,
  Transformer,
  Rect,
} from "react-konva";
import { TextElement, StickerElement, CaptionElement } from "../types";

// Component to handle CORS-enabled sticker loading
const StickerKonvaImage: React.FC<{
  sticker: StickerElement;
  onSelect: () => void;
  onUpdate: (updates: Partial<StickerElement>) => void;
}> = ({ sticker, onSelect, onUpdate }) => {
  const [loadedImage, setLoadedImage] = React.useState<HTMLImageElement | null>(
    null
  );

  useEffect(() => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => setLoadedImage(img);
    img.onerror = () =>
      console.error("Failed to load sticker image:", sticker.image_url);
    img.src = sticker.image_url;
  }, [sticker.image_url]);

  if (!loadedImage) return null;

  // Calculate dimensions that preserve aspect ratio
  const imageAspectRatio = loadedImage.width / loadedImage.height;
  const targetAspectRatio = sticker.width / sticker.height;

  let finalWidth = sticker.width;
  let finalHeight = sticker.height;

  // If the image is wider than the target aspect ratio, fit to width
  if (imageAspectRatio > targetAspectRatio) {
    finalHeight = sticker.width / imageAspectRatio;
  } else {
    // If the image is taller than the target aspect ratio, fit to height
    finalWidth = sticker.height * imageAspectRatio;
  }

  return (
    <KonvaImage
      id={sticker.id}
      image={loadedImage}
      x={sticker.x}
      y={sticker.y}
      width={finalWidth}
      height={finalHeight}
      rotation={sticker.rotation}
      scaleX={sticker.flipX === true ? -1 : 1}
      scaleY={sticker.flipY === true ? -1 : 1}
      draggable
      onClick={onSelect}
      onDragEnd={(e) => {
        onUpdate({
          x: e.target.x(),
          y: e.target.y(),
        });
      }}
      onTransformEnd={(e) => {
        const node = e.target;
        onUpdate({
          x: node.x(),
          y: node.y(),
          width: node.width() * Math.abs(node.scaleX()),
          height: node.height() * Math.abs(node.scaleY()),
          rotation: node.rotation(),
          flipX: node.scaleX() < 0,
          flipY: node.scaleY() < 0,
        });

        node.scaleX(sticker.flipX === true ? -1 : 1);
        node.scaleY(sticker.flipY === true ? -1 : 1);
      }}
    />
  );
};

interface MemeCanvasProps {
  templateImage: HTMLImageElement | null;
  texts: TextElement[];
  stickers: StickerElement[];
  captions: CaptionElement[];
  selectedEntity: string | null;
  creatorName: string | null;
  onMouseDown: (e: any) => void;
  onMouseMove: (e: any) => void;
  onMouseUp: () => void;
  onTextUpdate: (id: string, updates: Partial<TextElement>) => void;
  onStickerUpdate: (id: string, updates: Partial<StickerElement>) => void;
  onCaptionUpdate: (id: string, updates: Partial<CaptionElement>) => void;
  onEntitySelect: (id: string) => void;
  onCanvasReady?: (getCanvas: () => HTMLCanvasElement) => void;
  isMobile?: boolean;
}

const MemeCanvas: React.FC<MemeCanvasProps> = ({
  templateImage,
  texts,
  stickers,
  captions,
  selectedEntity,
  creatorName,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onTextUpdate,
  onStickerUpdate,
  onCaptionUpdate,
  onEntitySelect,
  onCanvasReady,
  isMobile = false,
}) => {
  const stageRef = useRef<any>(null);
  const transformerRef = useRef<any>(null);

  // Responsive stage sizing
  const stageSize = isMobile
    ? {
        width: Math.min(350, window.innerWidth - 32),
        height: Math.min(262, (window.innerWidth - 32) * 0.75),
      }
    : { width: 600, height: 450 };

  const creditBoxHeight = isMobile ? 30 : 40;
  const captionAreaHeight =
    captions.length > 0
      ? Math.max(
          ...captions.map((c) =>
            c.height <= 1 ? c.height * stageSize.height : c.height
          )
        )
      : 0;
  const totalStageHeight =
    stageSize.height + (creatorName ? creditBoxHeight : 0) + captionAreaHeight;

  useEffect(() => {
    // Update transformer when selection changes
    if (transformerRef.current) {
      const stage = stageRef.current?.getStage();
      if (selectedEntity && stage) {
        const selectedNode = stage.findOne(`#${selectedEntity}`);
        if (selectedNode) {
          transformerRef.current.nodes([selectedNode]);
          transformerRef.current.getLayer().batchDraw();
        }
      } else {
        transformerRef.current.nodes([]);
        transformerRef.current.getLayer().batchDraw();
      }
    }
  }, [selectedEntity]);

  useEffect(() => {
    // Expose canvas function to parent
    if (onCanvasReady && stageRef.current) {
      const getCanvas = () => {
        if (stageRef.current) {
          // stageRef.current is the Stage component, we need to get the actual Konva stage
          const stage = stageRef.current.getStage();
          if (stage) {
            return stage.toCanvas();
          }
        }
        throw new Error("Stage not available");
      };
      onCanvasReady(getCanvas);
    }
  }, [onCanvasReady]);

  return (
    <div className="flex justify-center">
      <div className="flex flex-col items-center">
        <Stage
          ref={stageRef}
          width={stageSize.width}
          height={totalStageHeight}
          onMouseDown={onMouseDown}
          onMousemove={onMouseMove}
          onMouseup={onMouseUp}
          className="border-2 border-gray-200 rounded-xl shadow-lg"
        >
          <Layer>
            {/* Caption Area Background */}
            {captions.length > 0 && (
              <Rect
                x={0}
                y={0}
                width={stageSize.width}
                height={captionAreaHeight}
                fill="#FFFFFF"
                stroke="#E5E7EB"
                strokeWidth={1}
              />
            )}

            {/* Caption Elements */}
            {captions.map((caption) => {
              // Convert relative coordinates to absolute pixels
              const captionX =
                caption.x <= 1 ? caption.x * stageSize.width : caption.x;
              const captionY =
                caption.y <= 1 ? caption.y * captionAreaHeight : caption.y;
              // Convert relative sizes to absolute pixels
              const fontSize =
                caption.fontSize <= 1
                  ? caption.fontSize * stageSize.height
                  : caption.fontSize;
              const strokeWidth =
                caption.strokeWidth <= 1
                  ? caption.strokeWidth * stageSize.height
                  : caption.strokeWidth;
              const captionWidth =
                caption.width <= 1
                  ? caption.width * stageSize.width
                  : caption.width;
              // Use caption alignment or default to center for backward compatibility
              const captionAlign = caption.align || "center";

              return (
                <Text
                  key={caption.id}
                  id={caption.id}
                  text={caption.text}
                  x={captionX}
                  y={captionY}
                  width={captionWidth}
                  fontSize={fontSize}
                  fontFamily={caption.fontFamily}
                  fill={caption.fill}
                  stroke={caption.stroke}
                  strokeWidth={strokeWidth}
                  align={captionAlign}
                  direction={caption.dir}
                  wrap="word"
                  draggable
                  dragBoundFunc={(pos) => {
                    // Constrain captions to stay within the caption area
                    return {
                      x: Math.max(
                        0,
                        Math.min(stageSize.width - captionWidth, pos.x)
                      ),
                      y: Math.max(0, Math.min(captionAreaHeight - 20, pos.y)),
                    };
                  }}
                  onClick={() => onEntitySelect(caption.id)}
                  onDragEnd={(e) => {
                    onCaptionUpdate(caption.id, {
                      // Store relative position (0-1) within the caption area
                      x: e.target.x() / stageSize.width,
                      y: e.target.y() / captionAreaHeight,
                    });
                  }}
                  onTransformEnd={(e) => {
                    const node = e.target;
                    onCaptionUpdate(caption.id, {
                      // Store relative font size
                      fontSize:
                        caption.fontSize * node.scaleY() <= 1
                          ? caption.fontSize * node.scaleY()
                          : (caption.fontSize * node.scaleY()) /
                            stageSize.height,
                      // Store relative width
                      width:
                        caption.width * node.scaleX() <= 1
                          ? caption.width * node.scaleX()
                          : (caption.width * node.scaleX()) / stageSize.width,
                    });

                    node.scaleX(1);
                    node.scaleY(1);
                  }}
                />
              );
            })}

            {templateImage &&
              (() => {
                // Calculate proper scaling to maintain aspect ratio
                const imageAspectRatio =
                  templateImage.width / templateImage.height;
                const stageAspectRatio = stageSize.width / stageSize.height;

                let imageWidth, imageHeight, imageX, imageY;

                if (imageAspectRatio > stageAspectRatio) {
                  // Image is wider than stage - fit to width
                  imageWidth = stageSize.width;
                  imageHeight = stageSize.width / imageAspectRatio;
                  imageX = 0;
                  imageY =
                    captionAreaHeight + (stageSize.height - imageHeight) / 2;
                } else {
                  // Image is taller than stage - fit to height
                  imageWidth = stageSize.height * imageAspectRatio;
                  imageHeight = stageSize.height;
                  imageX = (stageSize.width - imageWidth) / 2;
                  imageY = captionAreaHeight;
                }

                return (
                  <KonvaImage
                    image={templateImage}
                    x={imageX}
                    y={imageY}
                    width={imageWidth}
                    height={imageHeight}
                  />
                );
              })()}

            {texts.map((text) => {
              // Convert relative coordinates to absolute pixels
              const textX = text.x <= 1 ? text.x * stageSize.width : text.x;
              const textY = text.y <= 1 ? text.y * stageSize.height : text.y;
              // Convert relative sizes to absolute pixels
              const fontSize =
                text.fontSize <= 1
                  ? text.fontSize * stageSize.height
                  : text.fontSize;
              const strokeWidth =
                text.strokeWidth <= 1
                  ? text.strokeWidth * stageSize.height
                  : text.strokeWidth;
              // Convert relative width to absolute pixels for text wrapping
              const textWidth = text.width
                ? text.width <= 1
                  ? text.width * stageSize.width
                  : text.width
                : undefined;
              // Determine text alignment
              const textAlign =
                text.align || (text.dir === "rtl" ? "right" : "left");

              return (
                <Text
                  key={text.id}
                  id={text.id}
                  text={text.text}
                  x={textX}
                  y={textY + captionAreaHeight}
                  width={textWidth}
                  fontSize={fontSize}
                  fontFamily={text.fontFamily}
                  fill={text.fill}
                  stroke={text.stroke}
                  strokeWidth={strokeWidth}
                  align={textAlign}
                  direction={text.dir}
                  wrap={textWidth ? "word" : undefined}
                  draggable
                  onClick={() => onEntitySelect(text.id)}
                  onDragEnd={(e) => {
                    onTextUpdate(text.id, {
                      // Store relative position (0-1) within the main canvas area
                      x: e.target.x() / stageSize.width,
                      y: (e.target.y() - captionAreaHeight) / stageSize.height,
                    });
                  }}
                  onTransformEnd={(e) => {
                    const node = e.target;
                    const scaleX = node.scaleX();
                    const scaleY = node.scaleY();

                    // Calculate new width based on horizontal scaling
                    const newWidth = (text.width || 0.5) * scaleX;

                    onTextUpdate(text.id, {
                      // Store relative position (0-1) within the main canvas area
                      x: node.x() / stageSize.width,
                      y: (node.y() - captionAreaHeight) / stageSize.height,
                      // Store relative font size based on vertical scaling
                      fontSize:
                        text.fontSize * scaleY <= 1
                          ? text.fontSize * scaleY
                          : (text.fontSize * scaleY) / stageSize.height,
                      // Store relative width for text wrapping
                      width: Math.max(0.1, Math.min(1, newWidth)),
                    });

                    // Reset scale but keep the new width
                    node.scaleX(1);
                    node.scaleY(1);
                  }}
                />
              );
            })}

            {stickers.map((sticker) => {
              // Convert relative coordinates to absolute pixels
              const stickerX =
                sticker.x <= 1 ? sticker.x * stageSize.width : sticker.x;
              const stickerY =
                sticker.y <= 1 ? sticker.y * stageSize.height : sticker.y;
              // Convert relative sizes to absolute pixels
              const stickerWidth =
                sticker.width <= 1
                  ? sticker.width * stageSize.width
                  : sticker.width;
              const stickerHeight =
                sticker.height <= 1
                  ? sticker.height * stageSize.height
                  : sticker.height;

              return (
                <StickerKonvaImage
                  key={sticker.id}
                  sticker={{
                    ...sticker,
                    x: stickerX,
                    y: stickerY + captionAreaHeight,
                    width: stickerWidth,
                    height: stickerHeight,
                  }}
                  onSelect={() => onEntitySelect(sticker.id)}
                  onUpdate={(updates) => {
                    const adjustedUpdates = { ...updates };
                    if (updates.x !== undefined) {
                      // Store relative position (0-1) within the main canvas area
                      adjustedUpdates.x = updates.x / stageSize.width;
                    }
                    if (updates.y !== undefined) {
                      // Store relative position (0-1) within the main canvas area
                      adjustedUpdates.y =
                        (updates.y - captionAreaHeight) / stageSize.height;
                    }
                    if (updates.width !== undefined) {
                      // Store relative size (0-1) within the main canvas area
                      adjustedUpdates.width = updates.width / stageSize.width;
                    }
                    if (updates.height !== undefined) {
                      // Store relative size (0-1) within the main canvas area
                      adjustedUpdates.height =
                        updates.height / stageSize.height;
                    }
                    onStickerUpdate(sticker.id, adjustedUpdates);
                  }}
                />
              );
            })}

            {/* Creator Name Box */}
            {creatorName && (
              <Rect
                x={0}
                y={stageSize.height + captionAreaHeight}
                width={stageSize.width}
                height={creditBoxHeight}
                fill="#FFFFFF"
                stroke="#E5E7EB"
                strokeWidth={1}
              />
            )}

            {/* Creator Name Text */}
            {creatorName && (
              <Text
                text={`נוצר על ידי: ${creatorName}`}
                x={0}
                y={
                  stageSize.height +
                  captionAreaHeight +
                  creditBoxHeight / 2 -
                  10
                }
                fontSize={20}
                fontFamily="Arial"
                fill="#374151"
                width={stageSize.width}
                align="center"
                verticalAlign="middle"
              />
            )}

            <Transformer
              ref={transformerRef}
              boundBoxFunc={(oldBox, newBox) => {
                // Limit resize
                if (newBox.width < 5 || newBox.height < 5) {
                  return oldBox;
                }
                return newBox;
              }}
            />
          </Layer>
        </Stage>
      </div>
    </div>
  );
};

export default MemeCanvas;
